import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface AboutProps {
  lang: 'uk' | 'ru';
}

const content = {
  uk: {
    label: '(Про мене)',
    name: 'Ірина Сергеєнко',
    title: 'Практичний психолог · Марбелья',
    p1: 'Маю вищу психологічну освіту та сертифікати міжнародного зразка. Працюю в гештальт-підході.',
    p2: (
      <>
        За <span className="text-[#7EC8E3] font-semibold text-xl">15+ років</span> практики допомогла більш ніж <span className="text-[#7EC8E3] font-semibold text-xl">300 клієнтам</span> впоратися з тривожними розладами, депресією, кризами у стосунках та проблемами самооцінки. Спеціалізуюся на роботі з дорослими людьми та підлітками.
      </>
    ),
    p3: 'У своїй роботі використовую інтегративний підхід, поєднуючи методи когнітивно-поведінкової терапії, гештальт-терапії та екзистенційного аналізу. Вірю, що кожна людина має внутрішні ресурси для змін.',
    p4: 'Моя мета — створити безпечний простір, де ви зможете відкрито говорити про свої переживання та знайти шлях до внутрішньої гармонії. Я буду поруч на цьому шляху, підтримуючи та спрямовуючи вас.',
    alt: 'Ірина Сергеєнко — психолог',
  },
  ru: {
    label: '(Обо мне)',
    name: 'Ирина Сергеенко',
    title: 'Практический психолог · Марбелья',
    p1: 'Имею высшее психологическое образование и сертификаты международного образца. Работаю в гештальт-подходе.',
    p2: (
      <>
        За <span className="text-[#7EC8E3] font-semibold text-xl">15+ лет</span> практики помогла более чем <span className="text-[#7EC8E3] font-semibold text-xl">300 клиентам</span> справиться с тревожными расстройствами, депрессией, кризисами в отношениях и проблемами самооценки. Специализируюсь на работе со взрослыми людьми и подростками.
      </>
    ),
    p3: 'В своей работе использую интегративный подход, сочетая методы когнитивно-поведенческой терапии, гештальт-терапии и экзистенциального анализа. Верю, что каждый человек обладает внутренними ресурсами для изменений.',
    p4: 'Моя цель — создать безопасное пространство, где вы сможете открыто говорить о своих переживаниях и найти путь к внутренней гармонии. Я буду рядом на этом пути, поддерживая и направляя вас.',
    alt: 'Ирина Сергеенко — психолог',
  },
};

export default function About({ lang }: AboutProps) {
  const t = content[lang];
  const { ref: imgRef, isVisible: imgVisible } = useScrollAnimation();
  const { ref: textRef, isVisible: textVisible } = useScrollAnimation({ rootMargin: '0px 0px -40px 0px' });

  return (
    <section id="about" className="py-16 md:py-24 bg-[#FFF9F4] dark:bg-[#161210]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 items-center">
          {/* Photo */}
          <div
            ref={imgRef}
            className={`lg:col-span-2 transition-all duration-1000 ${imgVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}
          >
            <div className="rounded-[20px] md:rounded-[24px] overflow-hidden shadow-[0_12px_48px_rgba(168,218,220,0.2)] dark:shadow-[0_12px_48px_rgba(0,0,0,0.4)] max-w-sm mx-auto lg:max-w-none">
              <img
                src="https://static.readdy.ai/image/c26179b2c08d8211833738b4663bf6e2/fd5cb558c5a9e7e5abb7aa0f11c1234e.jpeg"
                alt={t.alt}
                className="w-full h-auto object-cover object-top"
              />
            </div>
          </div>

          {/* Text */}
          <div
            ref={textRef}
            className={`lg:col-span-3 transition-all duration-1000 ${textVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}
            style={{ transitionDelay: '100ms' }}
          >
            <div className="text-sm text-[#999999] dark:text-[#666666] mb-2 md:mb-3">{t.label}</div>

            <h2 className="text-3xl md:text-4xl font-light text-[#333333] dark:text-[#EEEEEE] mb-2">{t.name}</h2>
            <div className="text-xl md:text-2xl text-[#7EC8E3] font-light mb-6 md:mb-8">{t.title}</div>

            <div className="space-y-5 md:space-y-6 text-[16px] md:text-[17px] text-[#555555] dark:text-[#AAAAAA] leading-relaxed">
              <p>{t.p1}</p>

              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-[#A8DADC]"></div>
                <div className="w-2 h-2 rounded-full bg-[#A8DADC]"></div>
                <div className="w-2 h-2 rounded-full bg-[#A8DADC]"></div>
              </div>

              <p>{t.p2}</p>
              <p>{t.p3}</p>
              <p>{t.p4}</p>
            </div>

            <div className="mt-6 md:mt-8 text-right">
              <div className="text-xl md:text-2xl text-[#333333] dark:text-[#DDDDDD] font-serif italic">{t.name}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
